import { WorkflowOverview } from "~/organisms/WorkflowOverview";
import { DocumentMetadata } from "~/molecules/DocumentMetadata";
import { DocumentReaderConfirmation } from "~/molecules/DocumentReaderConfirmation";
import { useTranslation } from "react-i18next";
import type {
  DocumentDetails,
  DocumentDetailsRelease,
  User,
} from "~/api/types";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useDocumentRelease } from "~/api/v2/useDocumentRelease";
import { useReaderReport } from "~/api/v2/useReaderReport";
import { DiffSummary, useDiffSummaryVisible } from "~/molecules/DiffSummary";
import {
  canSeeAnchoringTab,
  canSeeWorkflowTab,
  isPublishedArtifact,
} from "~/helpers/documentPermissions";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { TabId, resolveTabState } from "~/molecules/DocumentPanelTabsState";
import { ReadersOverviewTab } from "~/organisms/ReadersOverviewTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/atoms/tabs";

interface DocumentPanelTabsProps {
  user: User;
  selectedRelease: DocumentDetailsRelease;
  document: DocumentDetails;
  onChange: () => void;
  onConfetti: () => void;
}

export const DocumentPanelTabs = ({
  user,
  selectedRelease,
  document,
  onChange,
  onConfetti,
}: DocumentPanelTabsProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParamsFromHook] = useSearchParams();

  const {
    data: releaseDetails,
    mutate: mutateRelease,
    error: releaseError,
    isLoading: isReleaseLoading,
  } = useDocumentRelease(selectedRelease?.id);
  const {
    data: readerReport,
    mutate: mutateReaderReport,
    error: readerReportError,
    isLoading: isReaderReportLoading,
  } = useReaderReport(selectedRelease?.id);

  const currentReader = readerReport?.readers.find((r) => r.userId === user.id);
  const isReader = currentReader !== undefined && !document.archivedAt;
  const hasRead = currentReader?.hasRead ?? false;

  const diffSummaryResult = useDiffSummaryVisible(
    selectedRelease,
    document.releases,
  );

  const showWorkflowTab = canSeeWorkflowTab(
    user.id,
    selectedRelease,
    releaseDetails,
  );
  const showReadingTab = isReader && isPublishedArtifact(selectedRelease);
  const showAnchoringTab = canSeeAnchoringTab(selectedRelease);

  const handleChange = () => {
    void mutateRelease();
    onChange();
  };

  const handleWorkflowPublish = (releaseId: number) => {
    handleChange();
    setSearchParamsFromHook((prev) => {
      const next = new URLSearchParams(prev);
      next.set("releaseId", String(releaseId));
      return next;
    });
    onConfetti();
  };

  const tabs = [
    showReadingTab
      ? {
          id: TabId.reading,
          label: t(($) => $.commons.workflow.reading),
          content: (
            <DocumentReaderConfirmation
              releaseId={selectedRelease.id}
              onConfirm={() => void mutateReaderReport()}
              isDone={hasRead}
            />
          ),
          badge: !hasRead && (
            <span className="bg-primary absolute top-1 right-1 z-1 size-3 animate-pulse rounded-full shadow-[0_0_10px_var(--primary)]" />
          ),
        }
      : null,
    showAnchoringTab
      ? {
          id: TabId.anchoring,
          label: t(($) => $.commons.workflow.anchoring),
          content: (
            <ReadersOverviewTab
              release={selectedRelease}
              category={document.category}
            />
          ),
        }
      : null,
    showWorkflowTab
      ? {
          id: TabId.workflow,
          label: t(($) => $.commons.workflows.workflow, { count: 1 }),
          content: (
            <WorkflowOverview
              releaseDetails={releaseDetails}
              selectedRelease={selectedRelease}
              onPublish={handleWorkflowPublish}
              onChange={handleChange}
            />
          ),
        }
      : null,
    diffSummaryResult.diffSummaryVisible
      ? {
          id: TabId.summary,
          label: t(($) => $.commons.workflow.diffSummary.tabTitle),
          content: (
            <DiffSummary
              documentId={document.id}
              artifactId={selectedRelease.artifact.id}
              currentReleaseNumber={selectedRelease.majorVersion}
              fileUpdatedAt={selectedRelease.artifact.fileUpdatedAt}
              previousReleaseInfo={diffSummaryResult.previousReleaseInfo}
            />
          ),
        }
      : null,
    {
      id: TabId.metadata,
      label: t(($) => $.commons.document.metadata),
      content: (
        <DocumentMetadata
          document={document}
          releaseId={selectedRelease.id}
          onSave={handleChange}
        />
      ),
    },
  ];

  const visibleTabs = tabs.filter((tab) => tab !== null);
  const tabFromUrl = searchParams.get("tab");
  const visibleTabIdsInDisplayOrder = visibleTabs.map((tab) => tab.id);
  const { activeTab, shouldSyncUrlTab } = resolveTabState({
    tabFromUrl,
    visibleTabIdsInDisplayOrder,
  });

  useEffect(() => {
    if (!shouldSyncUrlTab) {
      return;
    }

    setSearchParamsFromHook(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParamsFromHook, shouldSyncUrlTab]);

  const handleTabChange = (value: string) => {
    setSearchParamsFromHook((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    });
  };

  if (isReleaseLoading || isReaderReportLoading) {
    return <CenterSpinner />;
  }

  if (releaseError || readerReportError) {
    return <ErrorState />;
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      <TabsList>
        {visibleTabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className="min-w-0 data-[state=active]:min-w-max"
          >
            <span className="max-w-full truncate" title={tab.label}>
              {tab.label}
            </span>
            {tab.badge}
          </TabsTrigger>
        ))}
      </TabsList>

      {visibleTabs.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className="bg-card rounded-b-lg border-r border-b border-l px-4 py-6"
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};
